import { m } from "@estate-platform/i18n/messages";
import { useDevice } from "@estate-platform/ui/device";
import { NavList } from "@estate-platform/ui/nav-list";
import { createFileRoute, getRouteApi, Link, Navigate } from "@tanstack/react-router";

import { useDataLayer } from "../data/data-layer.tsx";
import { useViewer } from "../data/hooks.ts";
import { householdSections, isSectionShown, moreWorkSections, sections } from "../shell/navigation.ts";
import { roleLabel } from "../shell/roles.ts";

import type { Section } from "../shell/navigation.ts";
import type { ReactElement } from "react";

const route = getRouteApi("/_app");

const More = (): ReactElement => {
  const { deviceClass } = useDevice();
  const { auth } = useDataLayer();
  const viewer = useViewer();
  const { user } = route.useRouteContext();
  if (deviceClass !== "compact") {
    return <Navigate to="/today" replace />;
  }
  const item = (section: Section) => ({
    id: section.id,
    label: section.label(),
    icon: section.icon,
    render: <Link to={section.path} />,
    ...(section.isBrass === true && { isBrass: true }),
  });
  return (
    <div className="p-4">
      <NavList
        label={m.nav_more()}
        groups={[
          {
            label: m.nav_household(),
            items: householdSections
              .filter((section) => viewer !== null && isSectionShown(section, viewer.role))
              .map((section) => item(section)),
          },
          {
            label: m.nav_work(),
            items: moreWorkSections
              .filter((section) => viewer !== null && isSectionShown(section, viewer.role))
              .map((section) => item(section)),
          },
          {
            label: m.settings_group_account(),
            items: [item(sections.notifications), item(sections.settings)],
          },
          {
            items: [
              {
                id: "account",
                label: viewer?.displayName ?? user.email,
                caption: viewer === null ? "" : roleLabel(viewer.role),
                render: <Link to="/settings/$section" params={{ section: "profile" }} />,
              },
              {
                id: "sign-out",
                label: m.action_sign_out(),
                isDestructive: true,
                onSelect: () => {
                  void auth.signOut();
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/more")({
  component: More,
});
